/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { Button, InlineNotification, Link, Typography2 } from '@coursera/cds-core';
import { AttachmentIcon } from '@coursera/cds-icons';

import type { LearnerProfile } from 'bundles/account-profile/common/types';
import UploadButton from 'bundles/account-profile/components/common/UploadButton';
import type { AssemblyStatus } from 'bundles/account-profile/components/common/transloadit';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/account-profile';

const styles = {
  cont: () => css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-150);
  `,
  label: css`
    margin-bottom: var(--cds-spacing-50);
  `,
  section: () => css`
    display: flex;
    gap: var(--cds-spacing-150);
    align-items: center;
  `,
  progressCont: () => css`
    height: var(--cds-spacing-100);
    width: 100%;
    background: var(--cds-color-grey-50);
  `,
  progressInner: () => css`
    height: var(--cds-spacing-100);
    background: var(--cds-color-blue-700);
  `,
};

const TEMPLATE_PROFILE_RESUME_UPLOAD = '4290d88243b346d0b3198f5f0063783e';

export type Props = {
  resumeUrl?: string;
  profile?: Partial<LearnerProfile>;
  onChange: (resume?: { url?: string }) => void;
};

type State = {
  isUploading: boolean;
  progress: number;
  errorMessage?: string;
  fileName?: string;
};

const ProfileResumeUploader = (props: Props) => {
  const [uploadState, setUploadState] = useState<State>({
    isUploading: false,
    progress: 0,
  });

  const onUpload = () => {
    setUploadState((state) => ({ ...state, isUploading: true, progress: 0 }));
  };

  const onProgress = (progress: number) => {
    setUploadState((state) => ({ ...state, progress }));
  };

  const onSuccess = (response: AssemblyStatus) => {
    setUploadState((state) => ({
      ...state,
      isUploading: false,
      // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      fileName: response.uploads[0].name,
    }));

    props.onChange({
      // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      url: response.results.virus_scan[0].ssl_url,
    });
  };

  const onError = (error: string) => {
    setUploadState((state) => ({
      ...state,
      errorMessage: error,
      isUploading: false,
      progress: 0,
    }));
  };

  const removeResume = () => {
    setUploadState({ fileName: undefined, isUploading: false, progress: 0 });
    props.onChange({ url: '' });
  };

  const { profile } = props;
  const { isUploading, errorMessage, progress } = uploadState;

  if (!profile) {
    return null;
  }

  let label = props.resumeUrl ? _t('Change resume') : _t('Upload resume');
  if (isUploading) label = _t('Uploading...');

  const renderLabel = () => {
    // new resume just uploaded
    if (uploadState.fileName) {
      return (
        <div css={styles.section}>
          <AttachmentIcon />
          <Typography2 component="p">{uploadState.fileName}</Typography2>
        </div>
      );
    }
    // dialog opened and displaying an existing resume
    if (profile.resumeUrl && props.resumeUrl === profile.resumeUrl) {
      return (
        <div css={styles.section}>
          <AttachmentIcon />
          <Typography2 component="p">
            <Link href={profile.preSignedResumeUrl || profile.resumeUrl} target="_blank">
              {_t('My Resume')}
            </Link>
          </Typography2>
        </div>
      );
    }

    return null;
  };

  return (
    <div css={styles.cont}>
      <div>
        <Typography2 css={styles.label} component="h3" variant="subtitleMedium">
          {_t('Resume')}
        </Typography2>
        <Typography2 component="p" variant="bodySecondary">
          {_t('Have a resume ready? Add it here to make it easy for employers to view it.')}
        </Typography2>
      </div>
      {renderLabel()}
      <div css={styles.section} aria-live="polite">
        <UploadButton
          label={label}
          onUpload={onUpload}
          onProgress={onProgress}
          onSuccess={onSuccess}
          onError={onError}
          disabled={isUploading}
          templateId={TEMPLATE_PROFILE_RESUME_UPLOAD}
          accept="application/pdf"
          maxSize={5242880}
          fields={{
            externalId: profile.externalId,
          }}
        />
        {props.resumeUrl && (
          <Button
            trackingName="profile_resume_remove_button"
            withVisibilityTracking={false}
            requireFullyVisible={false}
            component={TrackedButton}
            size="small"
            variant="secondary"
            onClick={removeResume}
            aria-label={_t('Remove Resume')}
            disabled={isUploading}
          >
            {_t('Remove Resume')}
          </Button>
        )}
      </div>
      {isUploading && (
        <div css={styles.progressCont}>
          <div css={styles.progressInner} style={{ width: `${progress}%` }} />
        </div>
      )}
      <div>
        <Typography2 component="p" variant="bodySecondary">
          {_t('Maximum size of 5MB. PDF files only.')}
        </Typography2>
      </div>
      {!!errorMessage && <InlineNotification severity="error">{errorMessage}</InlineNotification>}
    </div>
  );
};

export default ProfileResumeUploader;
